/**
 * ConsentContext — centralized consent state for cookies + push reminder intent.
 *
 * Storage: localStorage key `vtc_consent_v1`
 *   {
 *     version: 1,
 *     acceptedAt: ISO string,
 *     optionalCookies: boolean,
 *     pushReminderIntent: boolean,     // just the user's expressed WILL ; actual browser permission is separate
 *     language: "fr"|"en"|...
 *   }
 *
 * Public API:
 *   - consent: object | null (null = not yet decided → banner must be shown)
 *   - open(): force-reopen the banner (used from footer link)
 *   - save(partial): persist the decision; closes the banner
 *   - reject(): shortcut to save { optionalCookies:false, pushReminderIntent:false }
 *   - acceptAll(): shortcut to save { optionalCookies:true, pushReminderIntent:true }
 *   - revokePush(): sets pushReminderIntent=false AND unsubscribes any active browser subscription
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { pickConsentLang } from "@/data/consentTranslations";

const STORAGE_KEY = "vtc_consent_v1";

const ConsentContext = createContext(null);

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.version === 1) return parsed;
    return null;
  } catch {
    return null;
  }
}

export function ConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readStored());
  const [bannerOpen, setBannerOpen] = useState(false);

  // Show the banner on first visit (no stored consent)
  useEffect(() => {
    if (!consent) {
      const t = setTimeout(() => setBannerOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [consent]);

  const open = useCallback(() => setBannerOpen(true), []);
  const close = useCallback(() => setBannerOpen(false), []);

  const save = useCallback((partial) => {
    const existing = readStored() || {};
    const next = {
      version: 1,
      acceptedAt: new Date().toISOString(),
      language: partial.language || existing.language || pickConsentLang(navigator?.language),
      optionalCookies: Boolean(partial.optionalCookies),
      pushReminderIntent: Boolean(partial.pushReminderIntent),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    setConsent(next);
    setBannerOpen(false);
  }, []);

  const reject = useCallback((language) => save({ optionalCookies: false, pushReminderIntent: false, language }), [save]);
  const acceptAll = useCallback((language) => save({ optionalCookies: true, pushReminderIntent: true, language }), [save]);

  const revokePush = useCallback(async () => {
    // Flip the stored flag
    const existing = readStored() || {};
    const next = { ...existing, version: 1, pushReminderIntent: false, acceptedAt: new Date().toISOString() };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
    setConsent(next);
    // Unsubscribe the browser from any active push
    try {
      if ("serviceWorker" in navigator && "PushManager" in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
    } catch {}
  }, []);

  const value = useMemo(() => ({
    consent,
    bannerOpen,
    hasDecided: !!consent,
    allowOptionalCookies: !!consent?.optionalCookies,
    allowPushReminder: !!consent?.pushReminderIntent,
    open,
    close,
    save,
    reject,
    acceptAll,
    revokePush,
  }), [consent, bannerOpen, open, close, save, reject, acceptAll, revokePush]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
}
